import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pipette } from "lucide-react";
import api from "../../lib/api";
import { recordsError } from "../../lib/records";
import { DEFAULT_REMOVAL, REMOVE_MODES, removeBackground, rgbToHex } from "../../lib/backgroundRemoval";
import { Segmented } from "./BlockEditors";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

const CHECKERS = { backgroundImage: "conic-gradient(#e2e8f0 25%, #fff 0 50%, #e2e8f0 0 75%, #fff 0)", backgroundSize: "16px 16px" };

function Range({ label, value, onChange, testid, hint }) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="flex items-center justify-between"><span className="font-medium text-slate-700">{label}</span><span className="text-xs text-slate-500">{value}</span></span>
      <input type="range" min={0} max={100} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-jamora" data-testid={testid} />
      {hint && <span className="block text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

// « Retirer le fond » : the picture on the left picks the background colour, the right one shows the result on a checkerboard. Only a
// preview — the saved image is computed by the server with the same settings, as a NEW image (the original is kept).
export default function RemoveBackgroundDialog({ asset, onClose, onCreated }) {
  const [settings, setSettings] = useState(DEFAULT_REMOVAL);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [empty, setEmpty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [name, setName] = useState(`${asset.name} (sans fond)`.slice(0, 120));
  const source = useRef(null);
  const target = useRef(null);
  const pixels = useRef(null);

  useEffect(() => {
    let live = true;
    api.get(`/records/brand/assets/${asset.id}/preview`).then(({ data }) => {
      const image = new Image();
      image.onload = () => {
        if (!live) return;
        const canvas = source.current;
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext("2d", { willReadFrequently: true });
        context.drawImage(image, 0, 0);
        pixels.current = context.getImageData(0, 0, canvas.width, canvas.height);
        setLoaded(true);
      };
      image.onerror = () => live && setLoadError("Image illisible.");
      image.src = data.image;
    }).catch((e) => live && setLoadError(recordsError(e, "Image introuvable.")));
    return () => { live = false; };
  }, [asset.id]);

  useEffect(() => {
    if (!loaded || !pixels.current) return;
    const { width, height, data } = pixels.current;
    const out = removeBackground(data, settings);
    setEmpty(!out);
    const canvas = target.current;
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, width, height);
    if (out) context.putImageData(new ImageData(out, width, height), 0, 0);
  }, [loaded, settings]);

  const set = (patch) => setSettings((s) => ({ ...s, ...patch }));
  const pick = (event) => {
    const canvas = source.current;
    const rect = canvas.getBoundingClientRect();
    const x = Math.min(canvas.width - 1, Math.max(0, Math.floor(((event.clientX - rect.left) / rect.width) * canvas.width)));
    const y = Math.min(canvas.height - 1, Math.max(0, Math.floor(((event.clientY - rect.top) / rect.height) * canvas.height)));
    const i = (y * canvas.width + x) * 4;
    set({ color: rgbToHex([pixels.current.data[i], pixels.current.data[i + 1], pixels.current.data[i + 2]]) });
  };

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/records/brand/assets/${asset.id}/remove-background`, { ...settings, name: name.trim() });
      toast.success("Image sans fond ajoutée à la médiathèque. L'originale est conservée.");
      await onCreated(data);
      onClose();
    } catch (e) {
      setError(recordsError(e, "Création impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="remove-bg-dialog">
        <DialogHeader>
          <DialogTitle>Retirer le fond</DialogTitle>
          <DialogDescription>Cliquez sur le fond de l'image pour choisir la couleur à retirer. Une nouvelle image est créée : l'originale reste dans la médiathèque.</DialogDescription>
        </DialogHeader>
        {loadError ? <p className="text-sm text-red-600" role="alert" data-testid="remove-bg-load-error">{loadError}</p> : (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <figure className="space-y-1">
                <figcaption className="flex items-center gap-1 text-xs font-medium text-slate-600"><Pipette size={12} />Image d'origine — cliquez sur le fond</figcaption>
                <div className="flex min-h-32 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 p-2">
                  {!loaded && <Loader2 className="animate-spin text-slate-400" size={18} />}
                  <canvas ref={source} onClick={pick} className={`max-h-56 max-w-full cursor-crosshair object-contain ${loaded ? "" : "hidden"}`} data-testid="remove-bg-source" aria-label="Image d'origine : cliquez sur le fond à retirer" />
                </div>
              </figure>
              <figure className="space-y-1">
                <figcaption className="text-xs font-medium text-slate-600">Résultat</figcaption>
                <div className="flex min-h-32 items-center justify-center rounded-lg border border-slate-200 p-2" style={CHECKERS}>
                  <canvas ref={target} className={`max-h-56 max-w-full object-contain ${loaded && !empty ? "" : "hidden"}`} data-testid="remove-bg-result" />
                  {empty && <p className="rounded bg-white/90 px-2 py-1 text-xs text-slate-600" data-testid="remove-bg-empty">Plus rien à garder : baissez la tolérance.</p>}
                </div>
              </figure>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-3">
                <Segmented value={settings.mode} choices={REMOVE_MODES} onChange={(mode) => set({ mode })} label="Méthode" testid="remove-bg-mode" />
                <p className="text-xs text-slate-500" data-testid="remove-bg-mode-hint">
                  {settings.mode === "ink" ? "Pour un cachet scanné sur papier : l'encre garde sa couleur, plus elle est foncée plus elle est opaque." : "Toute couleur proche du fond choisi devient transparente."}
                </p>
                <div className="flex items-center gap-2">
                  <input type="color" value={settings.color} onChange={(e) => set({ color: e.target.value })} aria-label="Couleur du fond à retirer" className="h-9 w-10 cursor-pointer rounded-md border border-slate-200 bg-white p-0.5" data-testid="remove-bg-color" />
                  <span className="text-sm text-slate-700">Fond à retirer</span>
                  <span className="font-mono text-xs text-slate-500">{settings.color}</span>
                </div>
              </div>
              <div className="space-y-3">
                <Range label="Tolérance" value={settings.tolerance} onChange={(tolerance) => set({ tolerance })} testid="remove-bg-tolerance" hint="Plus haut : davantage de nuances proches du fond sont retirées." />
                {settings.mode === "color" && <Range label="Bords adoucis" value={settings.softness} onChange={(softness) => set({ softness })} testid="remove-bg-softness" />}
              </div>
            </div>
            <label className="block space-y-1 text-sm"><span className="font-medium text-slate-700">Nom de la nouvelle image</span>
              <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} data-testid="remove-bg-name" />
            </label>
            <p className="text-xs text-slate-500">Fonctionne avec un fond uni (blanc, couleur plate). Un fond dégradé ou une photo ne se retire pas proprement.</p>
            {error && <p className="text-sm text-red-600" role="alert" data-testid="remove-bg-error">{error}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy || !loaded || empty || !name.trim()} data-testid="remove-bg-submit">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Créer l'image sans fond
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
